/**
 * AppShell — the full-viewport page frame for the evaluator redesign.
 *
 * Composes Sidebar (240px, always-dark) + a 44px topbar + a scrollable content
 * area. Purely presentational: no fetch, no polling, no global reads. The
 * topbar exposes two optional React slots (systemPill, themeToggle) that the
 * Wave 2c integration pass wires to live data.
 *
 * Observatory design ref §5.1 — shell structure: fixed inset-0, sidebar + main
 * column, topbar at bg-header/border, content at bg.
 */

import type { ReactNode } from 'react'
import { Sidebar } from './Sidebar'
import type { SidebarItem } from './Sidebar'

export interface AppShellProps {
  /** Nav items passed through to Sidebar. */
  nav: SidebarItem[]
  /** ID of the currently active nav item. */
  active: string
  /** Called when the user clicks a nav item. */
  onNav: (id: string) => void
  /**
   * Optional system/status pill rendered at the left of the topbar.
   * Wired in Wave 2c — absent here keeps the slot empty, not errored.
   */
  systemPill?: ReactNode
  /**
   * Optional theme toggle rendered at the right of the topbar.
   * Wired in Wave 2c — absent here keeps the slot empty, not errored.
   */
  themeToggle?: ReactNode
  children: ReactNode
}

export function AppShell({ nav, active, onNav, systemPill, themeToggle, children }: AppShellProps): JSX.Element {
  return (
    <div className="app-shell">
      <Sidebar items={nav} activeId={active} onSelect={onNav} />
      <div className="app-shell-main">
        <div className="app-shell-topbar">
          <div className="app-shell-topbar-pill">{systemPill}</div>
          <div className="app-shell-topbar-spacer" />
          <div className="app-shell-topbar-theme">{themeToggle}</div>
        </div>
        <main className="app-shell-content">{children}</main>
      </div>
    </div>
  )
}
